import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Shield, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

export default function GestionCategoriesCACES() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    libelle: '',
    validite_mois: 60
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await api.getCategoriesCACES();
      setCategories(data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      code: category.code,
      libelle: category.libelle,
      validite_mois: category.validite_mois || 60
    });
    setIsDialogOpen(true);
  };

  const handleNew = () => {
    setEditingCategory(null);
    setFormData({
      code: '',
      libelle: '',
      validite_mois: 60
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.libelle) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      if (editingCategory) {
        await api.updateCategorieCACES(editingCategory.id, formData);
        toast.success('Catégorie modifiée');
      } else {
        await api.createCategorieCACES(formData);
        toast.success('Catégorie créée');
      }
      setIsDialogOpen(false);
      loadCategories();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Supprimer la catégorie "${category.code}" ?`)) return;
    
    try {
      await api.deleteCategorieCACES(category.id);
      toast.success('Catégorie supprimée');
      loadCategories();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Catégories CACES et habilitations
          </h1>
          <p className="text-gray-600 mt-1">Gérer les catégories de CACES et habilitations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadCategories}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle catégorie CACES / Habilitation
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Liste des catégories</CardTitle>
          <CardDescription>{categories.length} catégories configurées</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Libellé</TableHead>
                <TableHead>Validité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell className="font-mono font-medium">{category.code}</TableCell>
                  <TableCell>{category.libelle}</TableCell>
                  <TableCell>{category.validite_mois || 60} mois</TableCell>
                  <TableCell>
                    <Badge variant={category.actif !== false ? "default" : "secondary"}>
                      {category.actif !== false ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(category)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(category)} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Modifier' : 'Nouvelle'} catégorie CACES / habilitation</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Modifier les informations de la catégorie' : 'Créer une nouvelle catégorie CACES / habilitation'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="Ex: R489-1, R486-A"
              />
            </div>
            <div className="space-y-2">
              <Label>Libellé *</Label>
              <Input
                value={formData.libelle}
                onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                placeholder="Ex: Chariot élévateur cat. 1"
              />
            </div>
            <div className="space-y-2">
              <Label>Durée de validité (mois)</Label>
              <Input
                type="number"
                value={formData.validite_mois}
                onChange={(e) => setFormData({ ...formData, validite_mois: parseInt(e.target.value) || 60 })}
              />
              <p className="text-xs text-gray-500">Standard: 60 mois (5 ans)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
